import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { INotificationBar, INotificationResponse } from '@common-types';
import { Urls } from '@share/constants';
import { axiosInstance, axiosInstanceB2B } from '@share/utils';
import { AppThunk } from '@share/utils';

export const UNREAD_TYPE = 'unread';
export const READ_TYPE = 'read';

export interface INotificationBarState {
  notificationBar: INotificationBar[] | null;
  notifications: INotificationResponse;
  totalNew: number;
  loading: boolean;
  error: string;
  count: number;
  unreadNotificationCount: number;
  readNotificationCount: number;
  unreadNotifications: INotificationResponse;
}

const initialState: INotificationBarState = {
  notificationBar: null,
  notifications: {} as INotificationResponse,
  loading: false,
  totalNew: 0,
  error: '',
  count: 0,
  readNotificationCount: 0,
  unreadNotificationCount: 0,
  unreadNotifications: {} as INotificationResponse,

};

const notificationBarSlice = createSlice({
  name: 'notificationBar',
  initialState,
  reducers: {
    setLoading: (state: INotificationBarState, { payload }: PayloadAction<boolean>) => {
      state.loading = payload;
    },
    setError: (state: INotificationBarState, { payload }: PayloadAction<string>) => {
      state.error = payload;
    },
    setTotalNew: (state: INotificationBarState, { payload }: PayloadAction<number>) => {
      state.totalNew = payload;
    },
    setCount: (state: INotificationBarState, { payload }: PayloadAction<number>) => {
      state.count = payload;
    },
    setReadNotificationCount: (state: INotificationBarState, { payload }: PayloadAction<number>) => {
      state.readNotificationCount = payload;
    },
    setUnreadNotificationCount: (state: INotificationBarState, { payload }: PayloadAction<number>) => {
      state.unreadNotificationCount = payload;
    },
    setUnreadNotificationItems: (
      state: INotificationBarState,
      { payload }: PayloadAction<INotificationResponse>
    ) => {
      state.unreadNotifications.items = payload.items;
      state.unreadNotifications.isEmpty = payload.isEmpty;
      state.unreadNotifications.currentPage = payload.currentPage;
      state.unreadNotifications.totalPages = payload.totalPages;
      state.unreadNotifications.isEmpty = payload.isEmpty;
      state.unreadNotifications.resultsPerPage = payload.resultsPerPage;
      state.unreadNotifications.totalResults = payload.totalResults;
    },
    setNotificationBar: (
      state: INotificationBarState,
      { payload }: PayloadAction<INotificationBar[]>,
    ) => {
      state.notificationBar = payload;
    },
    setNotificationItems: (
      state: INotificationBarState,
      { payload }: PayloadAction<INotificationResponse>
    ) => {
      state.notifications.items = payload.items;
      state.notifications.isEmpty = payload.isEmpty;
      state.notifications.currentPage = payload.currentPage;
      state.notifications.totalPages = payload.totalPages;
      state.notifications.isEmpty = payload.isEmpty;
      state.notifications.resultsPerPage = payload.resultsPerPage;
      state.notifications.totalResults = payload.totalResults;
    },
  },
});

export const notificationBarActions = notificationBarSlice.actions;

export const notificationBarReducer = notificationBarSlice.reducer;

export const getNotificationBar = (): AppThunk => {
  return async (dispatch) => {
    dispatch(notificationBarActions.setLoading(true));

    try {
      const res = await axiosInstance.get(Urls.NotificationBar);

      dispatch(notificationBarActions.setLoading(false));
      dispatch(notificationBarActions.setNotificationBar(res.data));
    } catch (error: any) {
      console.error(error);
      dispatch(notificationBarActions.setError(error.toString()));
      dispatch(notificationBarActions.setLoading(false));
    }
  };
};

export const getNotificationBarData = (userId?: string): AppThunk => {
  return async (dispatch, getState) => {

    try {
      const loginStore = getState();
      const { user } = loginStore;

      const url = `${Urls.Notification}/log?page=1&results=10&userId=${userId || user?.userId}&status=0`;
      const response = await axiosInstanceB2B.get(url);
      dispatch(notificationBarActions.setTotalNew(response.data.totalResults));
      dispatch(notificationBarActions.setUnreadNotificationItems(response.data));
    } catch (error: any) {
      console.error(error);
    }
  };
};

export const setNotification = (page: number = 1, results: number = 10, userId?: string, type?: string | null): AppThunk => {
  return async (dispatch) => {
    dispatch(notificationBarActions.setLoading(true));
    try {
      const sort = `&orderBy=created&sortOrder=desc`;
      const queryType = type ? `&status=${type === READ_TYPE ? '1' : '0'}` : '';
      const url = `${Urls.Notification}/log?page=${page}&results=${results}&userId=${userId}${sort}${queryType}`;
      const response = await axiosInstanceB2B.get(url);
      if (response?.data?.items) {
        dispatch(notificationBarActions.setLoading(false));
        dispatch(notificationBarActions.setNotificationItems(response.data));
        if (type === READ_TYPE) {
          dispatch(notificationBarActions.setReadNotificationCount(response.data.totalResults));
        }
        else if(type === UNREAD_TYPE) {
          dispatch(notificationBarActions.setUnreadNotificationCount(response.data.totalResults));
        }
      }

      dispatch(notificationBarActions.setLoading(false));
    } catch (error: any) {
      dispatch(notificationBarActions.setError(error.toString()));
      dispatch(notificationBarActions.setLoading(false));
    }
  };
};

export const deleteNotification = (notificationId: string, page: number = 1, results: number = 10, userId: string | undefined, type: string | null, onSuccess: () => void): AppThunk => {
  return async (dispatch) => {
    dispatch(notificationBarActions.setLoading(true));
    try {
      const isClearAll = notificationId === '-1';
      const url = `${Urls.Notification}/log/${isClearAll ? 'clearAll' : ''}`;
      const data = isClearAll ? { data: {} } : { data: { id: notificationId } };
      const response = await axiosInstanceB2B.delete(url, data);
      if (response?.data?.id) {
        dispatch(setNotification(page, results, userId, type));
        dispatch(getNotificationBarData(userId));
        onSuccess();
      } else {
        dispatch(notificationBarActions.setLoading(false));
      }
    } catch (error: any) {
      dispatch(notificationBarActions.setError(error.toString()));
      dispatch(notificationBarActions.setLoading(false));
    }
  };
};

export const markNotificationAsRead = (status: number, userId: string | undefined, onSuccess: () => void, onError?: (error: string) => void): AppThunk => {
  return async (dispatch) => {
    dispatch(notificationBarActions.setLoading(true));
    try {
      const url = `${Urls.MarkAsReadNotification}`;
      const response = await axiosInstanceB2B.put(url, {status : status});
      if(response?.data?.success === true){
        dispatch(getNotificationBarData(userId));
        dispatch(setNotification(1, 10, userId, UNREAD_TYPE));
        dispatch(notificationBarActions.setLoading(false));
        onSuccess();
      }
      else {
        dispatch(notificationBarActions.setLoading(false));
        onError?.("Something went wrong");
      }
    } catch (error: any) {
      dispatch(notificationBarActions.setError(error.toString()));
      dispatch(notificationBarActions.setLoading(false));
    }
  };
};